import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Store, SlidersHorizontal, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { CATEGORIES } from '../lib/categories';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ProductCard } from '../components/ProductCard';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { imgSrc } from '../lib/cart';
import { MapView } from '../components/MapView';

export default function Marketplace() {
  const [params] = useSearchParams();
  const sellerId = params.get('seller') || '';
  const [q, setQ] = useState(params.get('q') || '');
  const [cat, setCat] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [sort, setSort] = useState('recent');
  const [maxPrice, setMaxPrice] = useState('');
  const [products, setProducts] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/sellers/top', { params: { limit: 12 } }).then(({ data }) => setShops(data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/products', { params: { ...(q.length >= 2 ? { q } : {}), ...(cat ? { category: cat } : {}), ...(sellerId ? { seller_id: sellerId } : {}), sort, ...(maxPrice ? { max_price: maxPrice } : {}) } });
        setProducts(data);
      } catch {} finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat, sort, maxPrice, sellerId]);

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">Boutique</h1>
          <p className="text-slate-400 text-sm mt-1">Achetez directement les articles des vendeurs, sans attendre le live.</p>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input data-testid="shop-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un article, une marque…"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none focus:border-sky-400" />
          </div>
          <select data-testid="shop-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none">
            <option value="recent">Nouveautés</option><option value="price_asc">Prix croissant</option><option value="price_desc">Prix décroissant</option>
          </select>
          <div className="relative"><SlidersHorizontal className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input data-testid="shop-max-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Prix max (FCFA)" className="pl-9 pr-3 py-2.5 rounded-xl bg-slate-900 border border-slate-700 text-white outline-none w-44" /></div>
        </div>
        <div className="flex gap-2 overflow-x-auto pretty-scroll pb-1" data-testid="shop-categories">
          <button onClick={() => setCat('')} data-testid="shop-cat-all" className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border ${!cat ? 'btn-brand text-white border-transparent' : 'border-slate-700 text-slate-300'}`}>Tout</button>
          {CATEGORIES.map((c) => <button key={c.id} onClick={() => setCat(c.id)} data-testid={`shop-cat-${c.id}`} className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap border ${cat === c.id ? 'btn-brand text-white border-transparent' : 'border-slate-700 text-slate-300'}`}>{c.label}</button>)}
        </div>

        {shops.length > 0 && (
          <section>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="font-display text-lg font-bold flex items-center gap-2"><Store className="h-5 w-5 text-sky-400" />Boutiques</h2>
              {shops.some((s) => s.shop_lat != null) && <button onClick={() => setShowMap((v) => !v)} data-testid="toggle-shops-map" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full btn-ghost-gold"><MapPin className="h-3.5 w-3.5" />{showMap ? 'Masquer la carte' : 'Voir sur la carte'}</button>}
            </div>
            {showMap && <MapView className="mb-4" height="h-72" testid="shops-map" markers={shops.filter((s) => s.shop_lat != null).map((s) => ({ key: s.user_id, pos: [s.shop_lat, s.shop_lng], icon: 'shop', label: <Link to={`/seller/${s.user_id}`} className="font-bold">{s.shop_name || s.name}{s.shop_city ? ` · ${s.shop_city}` : ''}</Link> }))} />}
            <div className="flex gap-3 overflow-x-auto pretty-scroll pb-2" data-testid="shop-list">
              {shops.map((s) => (
                <Link key={s.user_id} to={`/seller/${s.user_id}`} data-testid={`shop-${s.user_id}`} className="min-w-[180px] rounded-2xl bg-slate-900/60 border border-slate-800 p-3 hover:border-sky-500/50 flex items-center gap-2">
                  {s.shop_logo && <img src={imgSrc(s.shop_logo)} alt="" className="h-10 w-10 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0">
                  <div className="font-bold truncate flex items-center gap-1.5">{s.shop_name || s.name}<VerifiedBadge verified={s.verified} testId={`shop-verified-${s.user_id}`} /></div>
                  <div className="text-xs text-slate-400">{s.shop_city ? `${s.shop_city} · ` : ''}{s.products_sold || 0} vente{(s.products_sold || 0) > 1 ? 's' : ''}{s.avg_rating ? ` · ★ ${s.avg_rating}` : ''}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-display text-lg font-bold mb-3">Articles <span className="text-slate-500 text-sm">({products.length})</span></h2>
          {loading ? <div className="text-slate-500 text-sm">Chargement…</div> : products.length === 0 ? (
            <div className="text-slate-500 text-sm" data-testid="shop-empty">Aucun article pour cette recherche.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="shop-grid">{products.map((p) => <ProductCard key={p.product_id} p={p} />)}</div>
          )}
        </section>
      </div>
      <Footer />
    </div>
  );
}
